import { useEffect, useState } from 'react';
import MentorshipService, { MentorshipNotificationPreferences } from './MentorshipService';

interface MentorshipNotificationsProps {
    cohortId: number | null;
    disabled?: boolean;
}

const ALL_ON: MentorshipNotificationPreferences = {
    mentorInvitationEmail: true,
    menteeOptInConfirmation: true,
    matchAnnouncement: true,
};

const clonePrefs = (value: MentorshipNotificationPreferences): MentorshipNotificationPreferences => ({
    mentorInvitationEmail: value.mentorInvitationEmail === true,
    menteeOptInConfirmation: value.menteeOptInConfirmation === true,
    matchAnnouncement: value.matchAnnouncement === true,
});

const prefsEqual = (a: MentorshipNotificationPreferences, b: MentorshipNotificationPreferences) =>
    a.mentorInvitationEmail === b.mentorInvitationEmail &&
    a.menteeOptInConfirmation === b.menteeOptInConfirmation &&
    a.matchAnnouncement === b.matchAnnouncement;

const MentorshipNotifications = ({ cohortId, disabled = false }: MentorshipNotificationsProps) => {
    const cohortAvailable = cohortId != null && !disabled;
    const [loaded, setLoaded] = useState<MentorshipNotificationPreferences | null>(null);
    const [draft, setDraft] = useState<MentorshipNotificationPreferences>(ALL_ON);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    useEffect(() => {
        if (!cohortAvailable || cohortId == null) {
            setLoaded(null);
            setDraft(ALL_ON);
            setErrorMessage('');
            setSuccessMessage('');
            setIsLoading(false);
            return;
        }

        let cancelled = false;
        const load = async () => {
            setIsLoading(true);
            setErrorMessage('');
            setSuccessMessage('');
            try {
                const next = await MentorshipService.getNotificationPreferences(cohortId);
                if (cancelled) return;
                const cloned = clonePrefs(next);
                setLoaded(cloned);
                setDraft(clonePrefs(next));
            } catch (error) {
                if (cancelled) return;
                setLoaded(null);
                setDraft(ALL_ON);
                setErrorMessage(MentorshipService.getErrorMessage(error));
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [cohortAvailable, cohortId]);

    const dirty = loaded != null && !prefsEqual(draft, loaded);
    const actionsDisabled = !cohortAvailable || isLoading || isSaving || loaded == null;

    const handleSave = async () => {
        if (cohortId == null || actionsDisabled || !dirty) return;
        setIsSaving(true);
        setErrorMessage('');
        setSuccessMessage('');
        try {
            const saved = await MentorshipService.saveNotificationPreferences(cohortId, draft);
            const cloned = clonePrefs(saved);
            setLoaded(cloned);
            setDraft(clonePrefs(saved));
            setSuccessMessage('Settings saved.');
        } catch (error) {
            setErrorMessage(MentorshipService.getErrorMessage(error));
        } finally {
            setIsSaving(false);
        }
    };

    const handleDiscard = () => {
        if (loaded == null) return;
        setDraft(clonePrefs(loaded));
        setErrorMessage('');
        setSuccessMessage('');
    };

    const toggle = (key: keyof MentorshipNotificationPreferences) => {
        setDraft((current) => ({ ...current, [key]: !current[key] }));
        setSuccessMessage('');
    };

    return (
        <div>
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                <h3 className="font-20 font-600 mb-0">Notifications</h3>
                <div className="d-flex flex-wrap gap-2">
                    <button type="button" className="btn-green text-white btn-sm font-13 font-700" disabled={actionsDisabled || !dirty} onClick={handleSave}>
                        <span>Save Settings</span>
                    </button>
                    <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm font-13 font-600 py-2 px-3 rounded-3 rounded-pill"
                        disabled={actionsDisabled || !dirty}
                        onClick={handleDiscard}>
                        Discard
                    </button>
                </div>
            </div>

            {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
            {successMessage && <div className="alert alert-success">{successMessage}</div>}

            {!cohortAvailable && <div className="text-muted mb-3">Select a cohort to manage Notifications.</div>}
            {cohortAvailable && isLoading && <div className="text-muted mb-3">Loading notification settings...</div>}

            {cohortAvailable && !isLoading && loaded != null && (
                <fieldset disabled={isSaving}>
                    <div className="card border-0 rounded-3 shadow-sm bg-white">
                        <div className="card-header bg-white border-bottom py-3 px-4 d-flex justify-content-between align-items-center">
                            <div className="card-title mb-0 font-16 font-700">Notification Settings</div>
                            <button className="btn btn-link text-primary font-14 font-600 p-0 text-decoration-none">
                                Preview Templates
                            </button>
                        </div>
                        <div className="card-body p-4">
                            <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                                <div>
                                    <label className="font-13 font-700 text-dark" htmlFor="mentorship-notify-invite">Mentor invitation email</label>
                                    <div className="font-11 text-secondary">Sent when admin invites an alumni to be a mentor</div>
                                </div>
                                <div className="form-check form-switch fs-5 mb-0">
                                    <input id="mentorship-notify-invite" className="form-check-input" type="checkbox" role="switch" checked={draft.mentorInvitationEmail}
                                onChange={() => toggle('mentorInvitationEmail')}/>
                                </div>
                            </div>
                            <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                                <div>
                                    <label className="font-13 font-700 text-dark" htmlFor="mentorship-notify-optin">Mentee opt-in confirmation</label>
                                    <div className="font-11 text-secondary">Sent to student on opt-in registration</div>
                                </div>
                                <div className="form-check form-switch fs-5 mb-0">
                                    <input id="mentorship-notify-optin" className="form-check-input" type="checkbox" role="switch" checked={draft.menteeOptInConfirmation}
                                onChange={() => toggle('menteeOptInConfirmation')}/>
                                </div>                        
                            </div>
                            <div className="d-flex justify-content-between align-items-center py-2">
                                <div>
                                    <label className="font-13 font-700 text-dark" htmlFor="mentorship-notify-match">Match announcement</label>
                                    <div className="font-11 text-secondary">Sent to both parties when admin publishes pairings</div>
                                </div>
                                <div className="form-check form-switch fs-5 mb-0">
                                    <input id="mentorship-notify-match" className="form-check-input" type="checkbox" role="switch" checked={draft.matchAnnouncement}
                                onChange={() => toggle('matchAnnouncement')}/>
                                </div>                        
                            </div>
                        </div>
                    </div>
                    
                </fieldset>
            )}
        </div>
    );
};

export default MentorshipNotifications;
